<template>
  <div v-if="hasBackups" id="backups" class="stretch">
    <el-button
      size="small"
      type="primary"
      class="action"
      :loading="loading"
      @click.prevent="backup"
    >
      Backup now
    </el-button>

    <el-divider content-position="left">
      <h3>Backups</h3>
    </el-divider>

    <el-table
      v-loading="loading || pending"
      ref="backups"
      size="mini"
      :element-loading-text="pending ? 'Backup in progress...' : ''"
      :data="data"
      :default-sort="tableSort"
    >
      <el-table-column width="14px" />

      <el-table-column label="Date" prop="time" sortable>
        <template slot-scope="scope">
          {{ scope.row.time ? fromNow(scope.row.time) : scope.row.name }}
        </template>
      </el-table-column>

      <el-table-column label="Objects">
        <template slot-scope="scope">
          {{ scope.row.objects }}
        </template>
      </el-table-column>

      <el-table-column label="Size">
        <template slot-scope="scope">
          {{ scope.row.size }}
        </template>
      </el-table-column>

      <el-table-column align="right">
        <template slot-scope="scope">
          <el-button
            icon="el-icon-upload2"
            type="warning"
            size="mini"
            @click.prevent="restore(scope.row.backup)"
          >
            Restore
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-button
      class="disable-backup"
      type="danger"
      size="mini"
      plain
      @click="disable"
    >
      Disable backup and remove all snapshots
    </el-button>
  </div>

  <el-card v-else class="align-center" shadow="hover">
    <h2>Make your application secure</h2>
    <p>When you enable backups, a system-level disk data of the application will be taken once a day during the night.<br>
      In the event of problems, you can restore from a point in time up to four weeks prior.</p>
    <el-button type="primary" @click="enable">
      Enable automatic backups <span>at an additional {{ $root.currency.symbol }}{{ cost }} /month.</span>
    </el-button>
  </el-card>
</template>

<script>
import { mapState } from 'vuex'

export default {
  props: {
    application: Object,
    instance: Object
  },
  data () {
    return {
      loading: false,
      data: [],
      tableSort: {
        order: 'descending',
        prop: 'time'
      }
    }
  },
  computed: {
    cost () {
      return Math.ceil(this.instance.storage.size / 10) + '.00'
    },
    hasBackups () {
      return this.application.config.backup === '1'
    },
    pending () {
      const re = new RegExp('^backup')
      return this.tasks.find((t) => t.applicationId === this.application.id && re.test(t.command))
    },
    ...mapState(['tasks'])
  },
  mounted () {
    if (this.hasBackups) {
      return this.load()
    }
  },
  methods: {
    load () {
      if (this.loading) {
        return
      }

      this.loading = true
      this.$store.dispatch('getApplicationBackups', this.application).then((res) => {
        this.loading = false
        this.data = res.data
      }).catch(() => {
        this.loading = false
      })
    },
    backup () {
      this.$confirm('Launching a backup can slow down the application. Continue?', 'Warning', {
        confirmButtonText: 'Backup Now',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => this.$store.dispatch('backupApplication', this.application))
        .then(() => this.$notify({
          title: 'Backup starting...',
          message: 'it may take some time depending on the size.',
          type: 'success'
        }))
        .catch(() => {})
    },
    restore (path) {
      const data = { ...this.application, path }
      const msg = 'Be careful this action is irreversible. To confirm, please enter RESTORE below.'
      this.$prompt(msg, 'Are you really sure ?', {
        confirmButtonText: 'Restore and override',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'el-button--warning',
        inputValidator: (value) => value === 'RESTORE',
        inputErrorMessage: 'Invalid input'
      }).then(() => this.$store.dispatch('restoreApplication', data))
        .then(() => this.$router.push('/instances/' + this.instance.id + '/' + this.application.id))
        .catch(() => {})
    },
    enable () {
      this.$confirm('The backup feature is billed monthly. Continue?', 'Warning', {
        confirmButtonText: 'Enable backup',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => this.$emit('save', { config: { ...(this.application.config || {}), backup: '1' } }))
        .catch(() => {})
    },
    disable () {
      this.$confirm('Disable backup will causes the deletion of all snapshots. Continue?', 'Warning', {
        confirmButtonText: 'Disable backup and remove snapshots',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'el-button--danger',
        type: 'warning'
      }).then(() => this.$emit('save', { config: { ...(this.application.config || {}), backup: '0' } }))
        .catch(() => {})
    }
  },
  watch: {
    pending (newVal, oldVal) {
      if (oldVal && !newVal) {
        setTimeout(() => this.load(), 1000)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  #backups {
    .el-divider {
      margin-left: 24px !important;
      width: calc(100% - 48px);
    }
    .action {
      right: 24px !important;
    }
  }
  .disable-backup {
    margin-top: 24px;
    margin-left: 24px;
  }
</style>
